import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { adminApi } from "./adminApi";
import { Calculator, Bot, Building2, CalendarCheck, CalendarClock, Database, Handshake, LayoutDashboard, ListFilter, LogOut, Mail, Network, Radar, Sprout, Wheat, Workflow } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/admin/accounts", label: "Accounts", icon: Building2, testid: "nav-accounts" },
  { to: "/admin/pipeline", label: "Pipeline", icon: Workflow, testid: "nav-pipeline" },
  { to: "/admin/campaigns", label: "Campaigns", icon: Sprout, testid: "nav-campaigns" },
  { to: "/admin/renewals", label: "Renewals", icon: CalendarClock, testid: "nav-renewals" },
  { to: "/admin/plan-builder", label: "Group Plan Builder", icon: Calculator, testid: "nav-plan-builder" },
  { to: "/admin/scheduling", label: "Scheduling", icon: CalendarCheck, testid: "nav-scheduling" },
  { to: "/admin/sequences", label: "Sequences", icon: Mail, testid: "nav-sequences" },
  { to: "/admin/harvest", label: "One Harvest Season", icon: Wheat, testid: "nav-harvest" },
  { to: "/admin/co-employer", label: "Co-Employer Radar", icon: Radar, testid: "nav-coemployer" },
  { to: "/admin/peo", label: "PEO signal", icon: Network, testid: "nav-peo" },
  { to: "/admin/peo-partners", label: "PEO partners", icon: Handshake, testid: "nav-peo-partners" },
  { to: "/admin/lists", label: "Target lists", icon: ListFilter, testid: "nav-lists" },
  { to: "/admin/data", label: "Data sources", icon: Database, testid: "nav-data" },
  { to: "/admin/bot-access", label: "Bot access", icon: Bot, testid: "nav-bot-access" },
];

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [newLeads, setNewLeads] = useState(0);

  useEffect(() => {
    adminApi.get("/crm/plan-builder/stats")
      .then(({ data }) => setNewLeads(data.new_leads || 0)).catch(() => setNewLeads(0));
  }, []);

  const signOut = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="flex min-h-screen bg-[#0E1A13] text-cream" data-testid="admin-shell">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-cream/10 bg-[#10211A] px-5 py-7 lg:flex">
        <div>
          <p className="font-serif text-xl leading-none text-cream">OneHarvestCo.</p>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">
            Growth Desk
          </p>
        </div>

        <nav className="mt-10 flex flex-1 flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-200 ${
                  isActive
                    ? "bg-cream/10 text-cream"
                    : "text-cream/55 hover:bg-cream/5 hover:text-cream"
                }`
              }
            >
              <Icon className="h-4 w-4" strokeWidth={1.6} />
              <span>{label}</span>
              {testid === "nav-plan-builder" && newLeads > 0 ? (
                <span data-testid="nav-plan-builder-badge"
                  className="ml-auto rounded-full bg-gold/20 px-2 py-0.5 font-mono text-[9px] text-gold">
                  {newLeads}
                </span>
              ) : null}            </NavLink>
          ))}
        </nav>

        <div className="border-t border-cream/10 pt-4">
          <p className="truncate text-sm text-cream" data-testid="admin-user-name">
            {user?.name || user?.email}
          </p>
          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40">
            {user?.role}
          </p>
          <button
            type="button"
            onClick={signOut}
            data-testid="admin-signout-button"
            className="mt-3 inline-flex items-center gap-2 text-xs text-cream/50 transition-colors duration-200 hover:text-terra"
          >
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.6} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-4 border-b border-cream/10 px-6 py-4 lg:hidden">
          <p className="font-serif text-lg">OneHarvestCo. Growth Desk</p>
          <button
            type="button"
            onClick={signOut}
            data-testid="admin-signout-button-mobile"
            className="text-xs text-cream/60"
          >
            Sign out
          </button>
        </header>
        <nav className="flex gap-1 overflow-x-auto border-b border-cream/10 px-4 py-2 lg:hidden">
          {NAV.map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-3 py-1.5 text-xs ${
                  isActive ? "bg-cream/10 text-cream" : "text-cream/50"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
        <main className="min-w-0 flex-1 px-5 py-7 sm:px-8 lg:px-10">{children}</main>
      </div>
    </div>
  );
};
